const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Global token getter function - will be set by App.js
let globalGetToken = null;

// Global trial expired callback - will be set by App.js
let globalOnTrialExpired = null;

// Default timeout for API calls (30 seconds)
const DEFAULT_TIMEOUT = 30000;

/**
 * Custom API Error class with additional context
 */
export class ApiError extends Error {
  constructor(message, status, code, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.isApiError = true;
  }
}

/**
 * Get user-friendly error message based on status code
 */
export function getErrorMessage(status, defaultMessage = 'Something went wrong') {
  const messages = {
    400: 'Invalid request. Please check your input.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You don\'t have permission to perform this action.',
    404: 'The requested resource was not found.',
    408: 'Request timed out. Please try again.',
    429: 'Too many requests. Please wait a moment and try again.',
    500: 'Server error. Please try again later.',
    502: 'Server temporarily unavailable. Please try again.',
    503: 'Service is temporarily unavailable. Please try again later.',
    504: 'Request timed out. Please try again.'
  };
  return messages[status] || defaultMessage;
}

/**
 * Set the auth token getter function (called from App.js)
 * @param {Function} getTokenFn - Clerk's getToken function
 */
export function setAuthTokenGetter(getTokenFn) {
  globalGetToken = getTokenFn;
}

/**
 * Set the trial expired callback function (called from App.js)
 * @param {Function} callback - Function to call when trial has expired
 */
export function setTrialExpiredCallback(callback) {
  globalOnTrialExpired = callback;
}

/**
 * Enhanced API call with proper error handling
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options plus custom options
 * @param {number} options.timeout - Request timeout in ms (default 30s)
 * @param {boolean} options.parseJson - Whether to parse response as JSON (default true)
 * @returns {Promise<Response|object>} - Response or parsed JSON
 */
export async function apiCall(endpoint, options = {}) {
  const { timeout = DEFAULT_TIMEOUT, parseJson = false, ...fetchOptions } = options;
  const savedAccount = localStorage.getItem('selectedGoogleAdsAccount');
  
  // Get auth token
  let token = null;
  if (globalGetToken) {
    try {
      token = await globalGetToken();
    } catch (error) {
      console.error('Failed to get auth token:', error);
    }
  }
  
  // Build URL and headers
  let url;
  const headers = { ...fetchOptions.headers };
  
  if (savedAccount) {
    try {
      const account = JSON.parse(savedAccount);
      
      // Parse endpoint to separate path and query params
      let path = endpoint;
      let queryString = '';
      
      if (endpoint.includes('?')) {
        [path, queryString] = endpoint.split('?');
      }
      
      // Build URL with path only
      url = new URL(`${API_BASE}${path}`);
      
      // Add existing query parameters
      if (queryString) {
        const existingParams = new URLSearchParams(queryString);
        existingParams.forEach((value, key) => {
          url.searchParams.set(key, value);
        });
      }
      
      // Add accountId
      url.searchParams.set('accountId', account.id);
      
      if (account.id) {
        headers['X-Account-Id'] = account.id;
      }
    } catch (e) {
      console.error('API call error parsing account:', e);
      url = `${API_BASE}${endpoint}`;
    }
  } else {
    console.warn('⚠️  No account selected, using default endpoint');
    url = `${API_BASE}${endpoint}`;
  }
  
  // Add auth header
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Auto-serialize plain object bodies as JSON. Strings are passed through
  // untouched so existing callers that already JSON.stringify() keep working.
  // FormData / Blob / URLSearchParams / ArrayBuffer are passed through too.
  if (
    fetchOptions.body !== undefined &&
    fetchOptions.body !== null &&
    typeof fetchOptions.body === 'object' &&
    !(fetchOptions.body instanceof FormData) &&
    !(fetchOptions.body instanceof Blob) &&
    !(fetchOptions.body instanceof URLSearchParams) &&
    !(typeof ArrayBuffer !== 'undefined' && fetchOptions.body instanceof ArrayBuffer)
  ) {
    fetchOptions.body = JSON.stringify(fetchOptions.body);
    if (!headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }
  }

  console.log(`🔗 API Call: ${url.toString()}`);

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url.toString(), {
      ...fetchOptions,
      headers,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // Return raw response if parseJson is false (backward compatible)
    if (!parseJson) {
      return response;
    }
    
    // Check if response is ok before parsing
    if (!response.ok) {
      let errorData = null;
      try {
        errorData = await response.json();
      } catch {
        // Response body is not JSON
      }
      
      // Check for trial expired error (429 with TRIAL_EXPIRED code)
      if (response.status === 429 && errorData?.code === 'TRIAL_EXPIRED' && globalOnTrialExpired) {
        globalOnTrialExpired();
      }
      
      const errorMessage = errorData?.error || errorData?.message || getErrorMessage(response.status);
      throw new ApiError(errorMessage, response.status, errorData?.code || 'API_ERROR', errorData);
    }
    
    // Parse JSON response
    try {
      return await response.json();
    } catch (parseError) {
      throw new ApiError('Invalid response from server', 500, 'PARSE_ERROR');
    }
    
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Handle abort/timeout
    if (error.name === 'AbortError') {
      throw new ApiError('Request timed out. Please try again.', 408, 'TIMEOUT');
    }
    
    // Handle network errors
    if (error.name === 'TypeError' && error.message?.includes('fetch')) {
      throw new ApiError(
        'Unable to connect to server. Please check your internet connection.',
        0,
        'NETWORK_ERROR'
      );
    }
    
    // Re-throw ApiError as-is
    if (error.isApiError) {
      throw error;
    }
    
    // Wrap unknown errors
    throw new ApiError(error.message || 'An unexpected error occurred', 500, 'UNKNOWN_ERROR');
  }
}

/**
 * Enhanced API call that always returns parsed JSON
 * Use this for simpler error handling in components
 */
export async function apiCallJson(endpoint, options = {}) {
  return apiCall(endpoint, { ...options, parseJson: true });
}

/**
 * Check if user is online
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Create a toast-friendly error object
 */
export function createErrorToast(error) {
  if (error.isApiError) {
    return {
      type: error.status === 401 || error.status === 403 ? 'warning' : 'error',
      title: error.status === 401 ? 'Session Expired' : 'Error',
      message: error.message,
      action: error.status === 401 ? { label: 'Sign In', onClick: () => window.location.reload() } : null
    };
  }
  return {
    type: 'error',
    title: 'Error',
    message: error.message || 'An unexpected error occurred'
  };
}

export { API_BASE };

